
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminAnalyticsData } from "@/types/analytics";
import { toast } from "sonner";

export function useAdminAnalytics() {
  return useQuery({
    queryKey: ['admin-analytics'],
    queryFn: async () => {
      try {
        console.log("Fetching basic analytics data...");
        const { data: basicData, error: basicError } = await supabase.rpc('get_admin_analytics');
        if (basicError) {
          console.error('Error fetching basic analytics:', basicError);
          toast.error("Failed to load basic analytics data");
          throw basicError;
        }

        if (!basicData || typeof basicData !== 'object') {
          console.error('Invalid data returned from get_admin_analytics');
          toast.error("Invalid analytics data format");
          throw new Error('Invalid data returned from get_admin_analytics');
        }

        console.log("Fetching enhanced analytics data...");
        const { data: enhancedData, error: enhancedError } = await supabase.rpc('get_enhanced_analytics');
        
        // If there's an error with enhanced analytics, we'll log it but continue with basic data
        if (enhancedError) {
          console.error('Error fetching enhanced analytics:', enhancedError);
          toast.warning("Some advanced analytics couldn't be loaded", {
            description: "Basic analytics data is still available"
          });
          
          // Create fallback data for enhanced analytics
          const fallbackEnhancedData = {
            totalSessions: 0,
            avgSessionDuration: 0,
            totalPageViews: 0,
            mostViewedPages: [],
            dailyActiveUsers: [],
          };
          
          const typedBasicData = basicData as Record<string, any>;
          
          // Combine with fallback data for enhanced metrics
          return {
            ...typedBasicData,
            ...fallbackEnhancedData
          } as AdminAnalyticsData;
        }

        if (!enhancedData || typeof enhancedData !== 'object') {
          console.error('Invalid data returned from get_enhanced_analytics');
          
          // Continue with basic data but use fallback for enhanced data
          const fallbackEnhancedData = {
            totalSessions: 0,
            avgSessionDuration: 0,
            totalPageViews: 0,
            mostViewedPages: [],
            dailyActiveUsers: [],
          };
          
          const typedBasicData = basicData as Record<string, any>;
          
          // Return combined data
          return {
            ...typedBasicData,
            ...fallbackEnhancedData
          } as AdminAnalyticsData;
        }

        const typedBasicData = basicData as Record<string, any>;
        const typedEnhancedData = enhancedData as Record<string, any>;
        
        console.log("Successfully fetched all analytics data");
        
        const combinedData: AdminAnalyticsData = {
          userCount: typedBasicData.userCount || 0,
          clientCount: typedBasicData.clientCount || 0,
          therapistCount: typedBasicData.therapistCount || 0,
          adminCount: typedBasicData.adminCount || 0,
          unassignedCount: typedBasicData.unassignedCount || 0,
          journalCount: typedBasicData.journalCount || 0,
          averageJournalsPerClient: typedBasicData.averageJournalsPerClient || 0,
          clientToTherapistRatio: typedBasicData.clientToTherapistRatio || 0,
          pageHits: typedBasicData.pageHits || 0,
          connectionRequests: typedBasicData.connectionRequests || 0,
          feedbackCount: typedBasicData.feedbackCount || 0,
          countryDistribution: Array.isArray(typedBasicData.countryDistribution) 
            ? typedBasicData.countryDistribution 
            : [],
          languageDistribution: Array.isArray(typedBasicData.languageDistribution)
            ? typedBasicData.languageDistribution 
            : [],
          therapistsWithConnections: Array.isArray(typedBasicData.therapistsWithConnections) 
            ? typedBasicData.therapistsWithConnections 
            : [],
          totalUploads: typedEnhancedData.totalUploads || 0,
          totalStorageMB: typedEnhancedData.totalStorageMB || 0,
          averageUploadsPerClient: typedEnhancedData.averageUploadsPerClient || 0,
          averageStoragePerClientMB: typedEnhancedData.averageStoragePerClientMB || 0,
          dailyActiveUsers: Array.isArray(typedEnhancedData.dailyActiveUsers) 
            ? typedEnhancedData.dailyActiveUsers 
            : [],
          totalSessions: typedEnhancedData.totalSessions || 0,
          avgSessionDuration: typedEnhancedData.avgSessionDuration || 0,
          totalPageViews: typedEnhancedData.totalPageViews || 0,
          mostViewedPages: Array.isArray(typedEnhancedData.mostViewedPages) 
            ? typedEnhancedData.mostViewedPages 
            : []
        };

        return combinedData;
      } catch (error) {
        console.error('Error fetching analytics:', error);
        toast.error("Failed to load analytics data");
        throw error;
      }
    },
    retry: 1,
    refetchOnWindowFocus: false
  });
}
